import apiClient from './api';
import { BuildingFacility, OperationTime, Building, Theme } from '../types';

export type { BuildingFacility };

export interface FacilityImage {
  url: string;
}

export interface FacilityDetail {
  id: number;
  name: string;
  description: string;
  purpose: string;
  equipment: string;
  caution: string;
  location: string;
  imageUrl: string;
  images: string[];
  operationTime: OperationTime[];
  building: Building;
  reservable: boolean;
  averageStarRating: number;
  category: string;
  themes: Theme[];
}

export interface BuildingFacilityDetail {
  id: number;
  name: string;
  hours: string;
  location: string;
  latitude: number;
  longitude: number;
  reviews: any[];
  [key: string]: any;
}

export interface FacilitySuggestion {
  tags: string[];
  facilities: {
    [tag: string]: any[];
  };
}

export interface FacilityCategory {
  category: string;
  facilities: any[];
}

export interface TimeSlot {
  startTime: string;
  durationMinutes: number;
  isBooked: boolean;
}

export interface ReservationInfoResponse {
  timeSlots: TimeSlot[];
  reservationLimitMinutes: number;
}

export interface DailySchedule {
  date: string;
  timeSlots: TimeSlot[];
}

export type WeeklySchedule = DailySchedule[];

export interface FacilityReview {
  id: number;
  content: string;
  startRating: number;
  images: string[];
  createdAt: string;
  member: {
    name: string;
    profileImageUrl: string;
  };
  facility?: {
    name: string;
    imageUrl: string;
    building: {
      name: string;
    };
  };
}

export interface FacilityReviewsResponse {
  content: FacilityReview[];
  last: boolean;
  totalElements: number;
  totalPages: number;
  first: boolean;
  size: number;
  number: number;
  numberOfElements: number;
  empty: boolean;
}

export interface ReviewStatistics {
  starRatings: {
    "1": number;
    "2": number;
    "3": number;
    "4": number;
    "5": number;
  };
  averageStarRating: number;
}

export interface LiveSeatInfo {
  facilityName: string;
  totalSeats: number;
  occupiedSeats: number;
}

export interface LiveSeatInfoResponse {
  content: LiveSeatInfo[];
  last: boolean;
  totalElements: number;
  totalPages: number;
  first: boolean;
  size: number;
  number: number;
  numberOfElements: number;
  empty: boolean;
}

export interface LiveTalkMember {
  name: string;
  profileImage: string;
}

export interface LiveTalk {
  id: number;
  message: string;
  createdAt: string;
  member: LiveTalkMember;
}

export interface LiveTalkResponse {
  content: LiveTalk[];
  last: boolean;
  totalElements: number;
  totalPages: number;
  first: boolean;
  size: number;
  number: number;
  numberOfElements: number;
  empty: boolean;
}

export const facilityService = {
  getFacilityImages: async (facilityId: number, page: number = 1): Promise<FacilityImage[]> => {
    const response = await apiClient.get<FacilityImage[]>(`/facility/${facilityId}/img`, {
      params: { page },
    });
    return response.data;
  },

  getFacilityDetail: async (facilityId: number): Promise<FacilityDetail> => {
    const response = await apiClient.get<FacilityDetail>(`/facilities/${facilityId}`);
    return response.data;
  },

  getBuildingFacilityImages: async (
    buildingId: number,
    page: number = 1
  ): Promise<FacilityImage[]> => {
    const response = await apiClient.get<FacilityImage[]>(
      `/facility/building/${buildingId}/img`,
      {
        params: { page },
      }
    );
    return response.data;
  },

  getBuildingFacilityDetail: async (buildingId: number): Promise<BuildingFacilityDetail> => {
    const response = await apiClient.get<BuildingFacilityDetail>(
      `/facility/building/${buildingId}`
    );
    return response.data;
  },

  getFacilitySuggestions: async (): Promise<FacilitySuggestion> => {
    const response = await apiClient.get<FacilitySuggestion>('/facility/suggestion');
    return response.data;
  },

  getFacilitiesByCategory: async (type: 'building' | 'theme'): Promise<FacilityCategory[]> => {
    const universityId = localStorage.getItem('universityId');
    const response = await apiClient.get<FacilityCategory[]>(`/universities/${universityId}/facilities`, {
      params: { type },
    });
    return response.data;
  },

  getFacilityCategoryBuilding: async (): Promise<any> => {
    const universityId = localStorage.getItem('universityId');
    const response = await apiClient.get(`/universities/${universityId}/facilities`);
    return response.data;
  },

  getBuildingFacilities: async (buildingId: number): Promise<BuildingFacility[]> => {
    const response = await apiClient.get<BuildingFacility[]>(`/buildings/${buildingId}/facilities`);
    return response.data;
  },

  getThemeFacilities: async (themeId: number): Promise<BuildingFacility[]> => {
    const response = await apiClient.get<BuildingFacility[]>(`/themes/${themeId}/facilities`);
    return response.data;
  },

  getFacilityTimes: async (facilityId: number, date: string): Promise<ReservationInfoResponse> => {
    const response = await apiClient.get<ReservationInfoResponse>(`/facilities/${facilityId}/reservation-info`, {
      params: { date },
    });
    return response.data;
  },

  getWeeklySchedule: async (facilityId: number, date: string): Promise<WeeklySchedule> => {
    const response = await apiClient.get<WeeklySchedule>(`/facilities/${facilityId}/weekly-schedule`, {
      params: { date },
    });
    return response.data;
  },

  getFacilityReviews: async (
    facilityId: number,
    page: number = 0,
    size: number = 10,
    sort: string = 'createdAt,DESC'
  ): Promise<FacilityReviewsResponse> => {
    const response = await apiClient.get<FacilityReviewsResponse>(
      `/facilities/${facilityId}/reviews`,
      { params: { page, size, sort } }
    );
    return response.data;
  },

  getReviewStatistics: async (facilityId: number): Promise<ReviewStatistics> => {
    const response = await apiClient.get<ReviewStatistics>(`/facilities/${facilityId}/reviews/statistics`);
    return response.data;
  },

  searchFacilities: async (keyword: string): Promise<FacilityDetail[]> => {
    const universityId = localStorage.getItem('universityId');
    const response = await apiClient.get<FacilityDetail[]>(`/universities/${universityId}/facilities/search`, {
      params: { keyword },
    });
    return response.data;
  },

  saveSearchLog: async (keyword: string): Promise<void> => {
    await apiClient.post('/me/search', { keyword });
  },

  getSearchHistory: async (): Promise<{ keyword: string }[]> => {
    const response = await apiClient.get<{ keyword: string }[]>('/me/search');
    return response.data;
  },

  deleteSearchHistory: async (keyword: string): Promise<void> => {
    await apiClient.delete('/me/search', {
      params: { keyword },
    });
  },

  getPopularSearches: async (size: number = 5): Promise<{ keyword: string }[]> => {
    const universityId = localStorage.getItem('universityId');
    if (!universityId) {
      throw new Error('University ID not found in localStorage');
    }
    const response = await apiClient.get<{ keyword: string }[]>(`/universities/${universityId}/search/popular`, {
      params: { size },
    });
    return response.data;
  },

  getLiveSeatInfo: async (page: number = 0, size: number = 5): Promise<LiveSeatInfoResponse> => {
    const universityId = localStorage.getItem('universityId');
    if (!universityId) {
      throw new Error('University ID not found in localStorage');
    }
    const response = await apiClient.get<LiveSeatInfoResponse>(`/universities/${universityId}/facilities/usage-status`, {
      params: { page, size },
    });
    return response.data;
  },

  getLiveTalk: async (buildingId: number, page: number = 0, size: number = 10): Promise<LiveTalkResponse> => {
    const response = await apiClient.get<LiveTalkResponse>(`/buildings/${buildingId}/live-talk`, {
      params: { page, size },
    });
    return response.data;
  },

  createLiveTalk: async (buildingId: number, message: string): Promise<void> => {
    await apiClient.post(`/buildings/${buildingId}/live-talk`, { message });
  },
};
